/**
 * MAIN-883: client-side helpers for the tax exemption certificate upload.
 *
 * The API now accepts only PDF, PNG and JPEG certificates up to 10 MB,
 * detected from the file's bytes (api/src/modules/userIdentity/
 * tax-cert-upload.js). The page used to accept any file and showed "Certificate
 * successfully uploaded" even when the API refused it, because Apollo's lazy
 * query resolves with an `error` instead of throwing. These helpers check the
 * file before it is sent and decide success only from the API's answer. They
 * live here, not in the component, because this repo has no React DOM testing
 * library. The server remains the authority; this is only a fast pre-check.
 */
import { fileToBase64 } from "../Menu/MenuAction/menuItemImage.utils";

export const MAX_TAX_CERT_BYTES = 10 * 1024 * 1024;

// react-dropzone `accept`: what the file picker offers and what drops pass.
export const TAX_CERT_ACCEPT = {
  "application/pdf": [".pdf"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
};
const EXTENSIONS = Object.values(TAX_CERT_ACCEPT).flat();

export const UNSUPPORTED_FILE_MESSAGE =
  "Please upload your certificate as a PDF, PNG or JPEG file.";
export const TOO_LARGE_MESSAGE = "That file is over 10 MB. Please choose a smaller one.";
export const EMPTY_FILE_MESSAGE = "That file is empty. Please choose another.";
export const SIGN_IN_AGAIN_MESSAGE = "Your session has expired. Please sign in again.";
export const UPLOAD_FAILED_MESSAGE = "Certificate upload failed. Please try again.";

// The bytes each supported type starts with, as the API checks them.
const SIGNATURES = [
  [0x25, 0x50, 0x44, 0x46, 0x2d], // %PDF-
  [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], // PNG
  [0xff, 0xd8, 0xff], // JPEG
];

/**
 * Checks a selected or dropped file's extension and size. The browser's MIME
 * type is not required: some Windows and mobile pickers report "" or a
 * variant for a real PDF, and the bytes are checked when the file is read.
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateTaxCertFile(file) {
  if (!file) return { ok: false, message: "No certificate was selected." };
  const name = String(file.name || "").toLowerCase();
  if (!EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return { ok: false, message: UNSUPPORTED_FILE_MESSAGE };
  }
  if (!file.size) return { ok: false, message: EMPTY_FILE_MESSAGE };
  if (file.size > MAX_TAX_CERT_BYTES) return { ok: false, message: TOO_LARGE_MESSAGE };
  return { ok: true };
}

/** True when the base64 content starts like a PDF, PNG or JPEG file. */
export function hasSupportedSignature(base64) {
  let head;
  try {
    head = atob(String(base64 || "").slice(0, 16));
  } catch {
    return false;
  }
  return SIGNATURES.some((signature) =>
    signature.every((byte, index) => head.charCodeAt(index) === byte),
  );
}

/**
 * Reads a validated file into the base64 the query sends, refusing one whose
 * content is not really a PDF, PNG or JPEG (a renamed file, for example).
 * @returns {Promise<{ ok: true, base64: string } | { ok: false, message: string }>}
 */
export function readTaxCertFile(file) {
  return fileToBase64(file).then(
    (base64) =>
      hasSupportedSignature(base64)
        ? { ok: true, base64 }
        : { ok: false, message: UNSUPPORTED_FILE_MESSAGE },
    () => {
      return { ok: false, message: "That file could not be read. Please choose another." };
    },
  );
}

/** The message for a drop react-dropzone rejected. */
export function rejectedFileMessage(rejection) {
  const codes = (rejection?.errors || []).map((error) => error.code);
  if (codes.includes("too-many-files")) return "Please choose one file.";
  if (codes.includes("file-too-large")) return TOO_LARGE_MESSAGE;
  return UNSUPPORTED_FILE_MESSAGE;
}

const MESSAGES_BY_CODE = {
  TAX_CERT_INVALID_TYPE: UNSUPPORTED_FILE_MESSAGE,
  TAX_CERT_EMPTY: EMPTY_FILE_MESSAGE,
  TAX_CERT_TOO_LARGE: TOO_LARGE_MESSAGE,
  ACCESS_DENIED: "You can only upload a certificate for your own account. Please sign in again.",
  UNAUTHENTICATED: SIGN_IN_AGAIN_MESSAGE,
  CUSTOMER_NOT_FOUND: "We couldn't find your account. Please sign in again or contact us.",
};

function messageForError(error) {
  // GraphQL errors arrive in graphQLErrors, or in the HTTP body when the
  // server answers with an error status.
  const errors = error?.graphQLErrors?.length
    ? error.graphQLErrors
    : error?.networkError?.result?.errors || [];
  const first = errors[0];
  const code = first?.extensions?.code;
  if (code && MESSAGES_BY_CODE[code]) return MESSAGES_BY_CODE[code];
  // isAuthenticated refuses a missing or expired session with this message.
  if (first?.message === "Not authenticated") return SIGN_IN_AGAIN_MESSAGE;
  return UPLOAD_FAILED_MESSAGE;
}

/**
 * Runs the upload query and reports success only when the API returned the
 * stored certificate's URL. A lazy query can resolve with `error` set or
 * reject outright; both are failures. Without a signed-in customer nothing is
 * sent.
 * @returns {Promise<{ ok: true, url: string } | { ok: false, message: string }>}
 */
export async function submitTaxCertificate(runUpload, variables) {
  if (!variables?.customerId) return { ok: false, message: SIGN_IN_AGAIN_MESSAGE };
  let result;
  try {
    result = await runUpload({ variables });
  } catch (error) {
    return { ok: false, message: messageForError(error) };
  }
  if (result?.error) return { ok: false, message: messageForError(result.error) };
  const url = result?.data?.uploadTaxCert?.url;
  return url ? { ok: true, url } : { ok: false, message: UPLOAD_FAILED_MESSAGE };
}
